import { QrCodeIcon } from "@heroicons/react/24/outline";
import { Button, Modal } from "north.js";
import React, { useRef, useState } from "react";
import styled from "styled-components";

import FastQrReader from "src/shared/components/FastQrReader";

type EmailQrScannerProps = {
  disabled: boolean;
  onScan: (text: string) => void;
  onClose: () => void;
};

const EmailQrScanner: React.FC<EmailQrScannerProps> = ({
  disabled,
  onScan,
  onClose,
}) => {
  const [open, setOpen] = useState(false);
  const [cameraError, setCameraError] = useState(false);
  const acceptingScanRef = useRef(false);

  const closeScanner = () => {
    acceptingScanRef.current = false;
    setOpen(false);
    onClose();
  };

  return (
    <>
      <ScanButton
        type="button"
        aria-label="Scan attendee ticket"
        title="Scan attendee ticket"
        disabled={disabled}
        onClick={() => {
          acceptingScanRef.current = true;
          setCameraError(false);
          setOpen(true);
        }}
      >
        <QrCodeIcon width={22} height={22} aria-hidden="true" />
      </ScanButton>
      <Modal open={open} onClose={closeScanner}>
        <Modal.Header>Scan attendee ticket</Modal.Header>
        <Modal.Body>
          <Instructions>
            The attendee&apos;s ticket contains their QR code.
          </Instructions>
          {open && (
            <FastQrReader
              videoId="registration-email-qr-video"
              onResult={(result, error) => {
                if (!acceptingScanRef.current) return;
                if (result) {
                  const text = result.getText().trim();
                  if (!text) return;
                  closeScanner();
                  onScan(text);
                } else if (
                  error &&
                  [
                    "NotAllowedError",
                    "PermissionDeniedError",
                    "NotFoundError",
                    "DevicesNotFoundError",
                    "NotReadableError",
                    "TrackStartError",
                    "OverconstrainedError",
                    "SecurityError",
                  ].includes(error.name)
                ) {
                  setCameraError(true);
                }
              }}
              containerStyle={{
                width: "100%",
                overflow: "hidden",
                borderRadius: 8,
              }}
              videoContainerStyle={{ paddingTop: "75%" }}
            />
          )}
          {cameraError && (
            <CameraError role="alert">
              Unable to access the camera. Check camera permissions, then reopen
              the scanner, or enter the email manually.
            </CameraError>
          )}
        </Modal.Body>
        <Modal.Footer justify="end">
          <Button color="secondary" onClick={closeScanner}>
            Cancel
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

const ScanButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  margin-right: -8px;
  padding: 0;
  border: 0;
  border-radius: 4px;
  background: transparent;
  color: ${({ theme }) => theme.globalConstants.color.textSecondary};
  cursor: pointer;
  pointer-events: auto;

  &:hover,
  &:focus-visible {
    background: ${({ theme }) => theme.globalConstants.color.borderGray};
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`;

const Instructions = styled.p`
  margin-bottom: 12px;
`;

const CameraError = styled.p`
  margin-top: 12px;
`;

export default EmailQrScanner;
